// AGC: Weather Service - Frontend API client for Open-Meteo weather data
// AGC: Handles API calls, caching, and offline fallback
// AGC: Tier-based forecast days: FREE (7), PRO/PREMIUM (14)

import type { WeatherData, CurrentWeather, HourlyForecast, DailyForecast, SprayConditions } from '../types'

/**
 * AGC: Weather API response from backend
 */
interface WeatherAPIResponse {
  success: boolean
  data: WeatherData
  meta: {
    source: string
    attribution: string
    attributionUrl: string
    tier: string
    forecastDays: number
    requestedAt: string
  }
}

/**
 * AGC: Cached weather data structure
 */
interface CachedWeather {
  data: WeatherData
  timestamp: number
  coordinates: { lat: number; lon: number }
  tier: string
}

import { getBackendUrl } from '../utils/backendUrl'

// AGC: Cache configuration
const CACHE_KEY_PREFIX = 'droidfarm_weather_'
const CACHE_DURATION_MS = 30 * 60 * 1000 // AGC: 30 minutes cache

/**
 * AGC: Generate cache key for coordinates
 */
const getCacheKey = (lat: number, lon: number, tier: string): string => {
  // AGC: Round to 2 decimal places for cache key
  const roundedLat = Math.round(lat * 100) / 100
  const roundedLon = Math.round(lon * 100) / 100
  return `${CACHE_KEY_PREFIX}${roundedLat}_${roundedLon}_${tier}`
}

/**
 * AGC: Save weather data to localStorage cache
 */
const cacheWeatherData = (lat: number, lon: number, tier: string, data: WeatherData): void => {
  try {
    const cacheKey = getCacheKey(lat, lon, tier)
    const cached: CachedWeather = {
      data,
      timestamp: Date.now(),
      coordinates: { lat, lon },
      tier,
    }
    localStorage.setItem(cacheKey, JSON.stringify(cached))
    console.log(`💾 AGC: Weather data cached for (${lat}, ${lon})`)
  } catch (error) {
    console.warn('⚠️ AGC: Failed to cache weather data:', error)
  }
}

/**
 * AGC: Get cached weather data if valid
 */
const getCachedWeatherData = (lat: number, lon: number, tier: string): WeatherData | null => {
  try {
    const cacheKey = getCacheKey(lat, lon, tier)
    const cachedStr = localStorage.getItem(cacheKey)
    
    if (!cachedStr) return null
    
    const cached: CachedWeather = JSON.parse(cachedStr)
    
    // AGC: Check if cache is still valid
    const age = Date.now() - cached.timestamp
    if (age > CACHE_DURATION_MS) {
      console.log(`🕐 AGC: Weather cache expired (${Math.round(age / 60000)} min old)`)
      localStorage.removeItem(cacheKey)
      return null
    }
    
    console.log(`📦 AGC: Using cached weather data (${Math.round(age / 60000)} min old)`)
    return cached.data
  } catch (error) {
    console.warn('⚠️ AGC: Failed to read weather cache:', error)
    return null
  }
}

/**
 * AGC: Fetch weather data from backend API
 * @param lat - Latitude
 * @param lon - Longitude  
 * @param tier - Subscription tier ('free', 'pro', 'premium')
 * @returns Weather data or throws error
 */
export const fetchWeatherData = async (
  lat: number,
  lon: number,
  tier: string = 'free'
): Promise<WeatherData> => {
  const backendUrl = getBackendUrl()
  const url = `${backendUrl}/api/v1/weather?lat=${lat}&lon=${lon}&tier=${tier}`
  
  console.log(`🌤️ AGC: Fetching weather from ${url}`)
  
  const response = await fetch(url)
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `Weather API error: ${response.status}`)
  }
  
  const result: WeatherAPIResponse = await response.json()
  
  if (!result.success) {
    throw new Error('Weather API returned unsuccessful response')
  }
  
  return result.data
}

/**
 * AGC: Get weather data with cache support
 * @param lat - Latitude
 * @param lon - Longitude
 * @param tier - Subscription tier
 * @param forceRefresh - Skip cache and fetch fresh data
 * @returns Weather data
 */
export const getWeatherData = async (
  lat: number,
  lon: number,
  tier: string = 'free',
  forceRefresh: boolean = false
): Promise<WeatherData> => {
  // AGC: Check cache first (unless forcing refresh)
  if (!forceRefresh) {
    const cached = getCachedWeatherData(lat, lon, tier)
    if (cached) {
      return cached
    }
  }
  
  // AGC: Check if online
  if (!navigator.onLine) {
    // AGC: Try to return any cached data for nearby location
    const anyCache = getCachedWeatherData(lat, lon, tier)
    if (anyCache) {
      console.log('📴 AGC: Offline - using cached weather data')
      return anyCache
    }
    throw new Error('Sem conexão com internet e sem dados em cache')
  }
  
  try {
    const data = await fetchWeatherData(lat, lon, tier)
    
    // AGC: Cache the fresh data
    cacheWeatherData(lat, lon, tier, data)
    
    return data
  } catch (error) {
    // AGC: On error, try to return cached data
    const cached = getCachedWeatherData(lat, lon, tier)
    if (cached) {
      console.warn('⚠️ AGC: API error, using cached data:', error)
      return cached
    }
    throw error
  }
}

/**
 * AGC: Get current weather only
 */
export const getCurrentWeather = async (
  lat: number,
  lon: number,
  tier: string = 'free'
): Promise<CurrentWeather> => {
  const data = await getWeatherData(lat, lon, tier)
  return data.current
}

/**
 * AGC: Get spray conditions
 */
export const getSprayConditions = async (
  lat: number,
  lon: number,
  tier: string = 'free'
): Promise<SprayConditions> => {
  const data = await getWeatherData(lat, lon, tier)
  return data.sprayConditions
}

/**
 * AGC: Get forecast days based on tier
 */
export const getForecastDays = (tier: string): number => {
  switch (tier.toLowerCase()) {
    case 'pro':
    case 'premium':
      return 14
    case 'free':
    default:
      return 7
  }
}

/**
 * AGC: Clear weather cache
 */
export const clearWeatherCache = (): void => {
  try {
    const keys = Object.keys(localStorage)
    let cleared = 0
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key)
        cleared++
      }
    })
    
    console.log(`🗑️ AGC: Cleared ${cleared} cached weather entries`)
  } catch (error) {
    console.warn('⚠️ AGC: Failed to clear weather cache:', error)
  }
}

/**
 * AGC: Get user's current location via GPS
 * AGC: Uses nativeGeolocation service -- Capacitor plugin on native, browser API on web.
 * @returns Promise with coordinates
 */
export const getGPSLocation = async (): Promise<{ lat: number; lon: number }> => {
  const { getCurrentPosition } = await import('./nativeGeolocation')
  const coords = await getCurrentPosition({
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 60000,
  })
  return { lat: coords.latitude, lon: coords.longitude }
}

/**
 * AGC: Geocode city/state to coordinates using Open-Meteo Geocoding API
 * @param city - City name
 * @param state - State name or abbreviation (optional)
 * @returns Promise with coordinates or null if not found
 */
export const geocodeLocation = async (
  city: string,
  state?: string | null
): Promise<{ lat: number; lon: number; name: string } | null> => {
  try {
    // AGC: Build search query
    const searchQuery = state ? `${city}, ${state}` : city
    const encodedQuery = encodeURIComponent(searchQuery)
    
    // AGC: Use Open-Meteo Geocoding API (free, no key required)
    const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodedQuery}&count=1&language=pt&country=Brazil`
    
    console.log(`🌍 AGC: Geocoding location: ${searchQuery}`)
    
    const response = await fetch(url)
    
    if (!response.ok) {
      console.warn('⚠️ AGC: Geocoding API error:', response.status)
      return null
    }
    
    const data = await response.json()
    
    if (!data.results || data.results.length === 0) {
      console.warn(`⚠️ AGC: No geocoding results for: ${searchQuery}`)
      return null
    }
    
    const result = data.results[0]
    console.log(`📍 AGC: Geocoded ${searchQuery} to (${result.latitude}, ${result.longitude})`)
    
    return {
      lat: result.latitude,
      lon: result.longitude,
      name: result.name + (result.admin1 ? `, ${result.admin1}` : ''),
    }
  } catch (error) {
    console.error('❌ AGC: Geocoding error:', error)
    return null
  }
}

/**
 * AGC: Get current location - tries GPS first, then falls back to profile location
 * @param profileCity - City from user profile (optional)
 * @param profileState - State from user profile (optional)
 * @returns Promise with coordinates
 */
export const getCurrentLocation = async (
  profileCity?: string | null,
  profileState?: string | null
): Promise<{ lat: number; lon: number; source: 'gps' | 'profile' | 'default' }> => {
  // AGC: Step 1 - Try GPS location first
  try {
    console.log('📍 AGC: Trying GPS location...')
    const gpsLocation = await getGPSLocation()
    console.log(`✅ AGC: GPS location: (${gpsLocation.lat}, ${gpsLocation.lon})`)
    return { ...gpsLocation, source: 'gps' }
  } catch (gpsError) {
    console.warn('⚠️ AGC: GPS unavailable:', gpsError)
  }
  
  // AGC: Step 2 - Try user profile location (geocode city/state)
  if (profileCity) {
    console.log(`📍 AGC: Trying profile location: ${profileCity}, ${profileState || ''}`)
    const geocoded = await geocodeLocation(profileCity, profileState)
    if (geocoded) {
      console.log(`✅ AGC: Profile location: (${geocoded.lat}, ${geocoded.lon})`)
      return { lat: geocoded.lat, lon: geocoded.lon, source: 'profile' }
    }
  }
  
  // AGC: Step 3 - Return default coordinates (Cristalina, GO - major farming region)
  console.log('📍 AGC: Using default location (Cristalina, GO)')
  return { 
    lat: -16.768, 
    lon: -47.613, 
    source: 'default' 
  }
}

export default {
  fetchWeatherData,
  getWeatherData,
  getCurrentWeather,
  getSprayConditions,
  getForecastDays,
  clearWeatherCache,
  getCurrentLocation,
  getGPSLocation,
  geocodeLocation,
}

